import React, { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { TrendingUp, Mail, Lock, Loader2, ShieldOff } from 'lucide-react';
import { apiFetch } from '@/auth/apiClient';
import { API_BASE } from '@/env';

const REDIRECT_KEY = 'ss.auth.redirect';

function navigateAfterAuth() {
  let target = '';
  try {
    target = localStorage.getItem(REDIRECT_KEY) || '';
    if (target) localStorage.removeItem(REDIRECT_KEY);
  } catch {
    /* swallow */
  }
  // Default landing after auth is the dashboard (which resolves to scanner).
  window.location.hash = target || '#/dashboard';
}

export const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // null = still checking. The check is advisory UX only — the server rejects
  // a disabled registration regardless, so a failed check fails open.
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API_BASE}/api/auth/registration-status`)
      .then((r) => (r.ok ? r.json() : { enabled: true }))
      .then((d) => { if (!cancelled) setRegistrationEnabled(d?.enabled !== false); })
      .catch(() => { if (!cancelled) setRegistrationEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await register(email.trim(), password);
      navigateAfterAuth();
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-lg shadow-primary/20 mb-3">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <div className="text-xl font-bold tracking-tight">
            <span>Trade</span>
            <span className="text-primary">Execute</span>
          </div>
          <span className="text-xs text-muted-foreground mt-1">Create a new account</span>
        </div>

        {registrationEnabled === null ? (
          <div className="rounded-xl border bg-card p-10 shadow-lg flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : registrationEnabled === false ? (
          <div className="rounded-xl border bg-card p-6 shadow-lg text-center space-y-3">
            <div className="mx-auto h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <ShieldOff className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium">Registration is closed</div>
            <p className="text-xs text-muted-foreground">
              New account sign-ups are currently disabled. Please check back later, or
              email{' '}
              <a href="mailto:support@tradeexecute.com" className="text-primary hover:underline">
                support@tradeexecute.com
              </a>{' '}
              to request access.
            </p>
            <div className="text-xs text-muted-foreground pt-1">
              Already have an account?{' '}
              <a href="#/login" className="text-primary hover:underline font-medium">
                Sign in
              </a>
            </div>
          </div>
        ) : (
        <form
          onSubmit={onSubmit}
          className="rounded-xl border bg-card p-6 shadow-lg space-y-4"
        >
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                placeholder="At least 8 characters"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Confirm password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…
              </>
            ) : (
              'Create account'
            )}
          </Button>

          <p className="text-center text-[11px] leading-relaxed text-muted-foreground pt-1">
            By creating an account you agree to the{' '}
            <a href="#/legal/terms" className="text-primary hover:underline">Terms of Service</a>,{' '}
            <a href="#/legal/privacy" className="text-primary hover:underline">Privacy Policy</a> and{' '}
            <a href="#/legal/risk" className="text-primary hover:underline">Risk Disclaimer</a>.
          </p>

          <div className="text-center text-xs text-muted-foreground pt-1">
            Already have an account?{' '}
            <a
              href="#/login"
              className="text-primary hover:underline font-medium"
            >
              Sign in
            </a>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
